import React, { useState } from 'react';
import DataMigrationService from '../services/dataMigration';

interface DataMigrationProps {
  isOpen?: boolean;
  onClose?: () => void;
  onComplete?: () => void;
}

export const DataMigration: React.FC<DataMigrationProps> = ({ isOpen, onClose, onComplete }) => {
  const [isMigrating, setIsMigrating] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const handleMigrate = async () => {
    setIsMigrating(true);
    setError('');
    setResults(null);

    try {
      const migrationResults = await DataMigrationService.migrateAll();
      setResults(migrationResults);

      // Optionally clear localStorage after successful migration
      if (Object.values(migrationResults).every((r: any) => r.success)) {
        DataMigrationService.clearLocalStorage();
        if (onComplete) onComplete();
      }
    } catch (err: any) {
      setError(err.message || 'Migration failed');
    } finally {
      setIsMigrating(false);
    }
  };

  if (isOpen === false) return null;

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white/90 dark:bg-surface-dark/90 backdrop-blur-2xl rounded-[2.5rem] shadow-2xl border border-white/20">
      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-1">System Protocol</p>
          <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight uppercase">Data Migration</h2>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-gray-50 dark:bg-background-dark/50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all border border-gray-100 dark:border-gray-800"
          >
            <span className="material-icons-round">close</span>
          </button>
        )}
      </div>

      <div className="mb-10 bg-gray-50 dark:bg-background-dark/50 p-6 rounded-3xl border border-gray-100 dark:border-gray-800">
        <p className="text-xs font-medium text-gray-500 mb-6 leading-relaxed">
          Initialize secure transfer of legacy local data to the Cloudflare optimized backend. This manifest includes:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800">
            <span className="material-icons-round text-primary text-sm">pets</span>
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Patients</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800">
            <span className="material-icons-round text-primary text-sm">inventory_2</span>
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Inventory</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800">
            <span className="material-icons-round text-primary text-sm">medical_services</span>
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Services</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800">
            <span className="material-icons-round text-primary text-sm">badge</span>
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Personnel</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 text-red-500 px-6 py-4 rounded-2xl mb-8 flex items-center gap-3">
          <span className="material-icons-round text-lg">error_outline</span>
          <p className="text-[10px] font-black uppercase tracking-tight">{error}</p>
        </div>
      )}

      {results && (
        <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 text-primary-dark dark:text-primary-light px-8 py-6 rounded-3xl mb-10">
          <h3 className="text-xs font-black uppercase tracking-[0.2em] mb-4 text-center">Migration Manifest Sync</h3>
          <div className="space-y-3">
            {Object.entries(results).map(([key, value]: [string, any]) => (
              <div key={key} className="flex justify-between items-center bg-white dark:bg-surface-dark px-4 py-2 rounded-xl border border-gray-100 dark:border-gray-800">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{key}</span>
                <span className={`text-[10px] font-black uppercase ${value.success ? 'text-green-500' : 'text-red-500'}`}>{value.success ? 'Synced' : 'Failed'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleMigrate}
        disabled={isMigrating}
        className="group w-full bg-primary hover:bg-primary-dark text-white font-black py-5 rounded-3xl shadow-2xl shadow-primary/30 transition-all flex items-center justify-center gap-3 disabled:opacity-30 active:scale-95 uppercase text-[11px] tracking-[0.2em]"
      >
        {isMigrating ? (
          <>
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
            Syncing...
          </>
        ) : (
          <>
            Start System Sync
            <span className="material-icons-round text-lg animate-pulse">sync</span>
          </>
        )}
      </button>
    </div>
  );
};